import { useState } from 'react';
import { Search, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ts } from './api';
import { Section, Pill, Empty } from './ui';

const US_STATES = 'AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY'.split(' ');

/**
 * Search available US numbers and put them in the company's cart.
 * The cart lives on the server, so it survives refreshes and can never hold
 * more numbers than seats or the same number twice. `onPick` switches the
 * picker into single-choice mode (re-picking an unavailable number).
 */
export function NumberSearch({ cart, onCart, onPick }) {
  const [mode, setMode] = useState('area_code');
  const [query, setQuery] = useState('');
  const [state, setState] = useState('CA');
  const [results, setResults] = useState([]);
  const [message, setMessage] = useState('');
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const search = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setSelected([]);
    try {
      const params = mode === 'area_code' ? { area_code: query } : mode === 'state' ? { state } : { city: query, state };
      const data = await ts.searchNumbers({ ...params, limit: 20 });
      setResults(data.numbers);
      setMessage(data.message || '');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const inCart = new Set((cart?.items || []).map(i => i.e164));
  // The main number is picked in its own step; this picker fills the seats.
  const toggle = (n) => setSelected(s => s.includes(n.e164) ? s.filter(x => x !== n.e164) : [...s, n.e164]);

  const add = async (nums) => {
    setBusy(true);
    try {
      const items = results.filter(r => nums.includes(r.e164));
      onCart(await ts.cartAdd(items));
      setSelected([]);
      toast.success(`${items.length} number${items.length > 1 ? 's' : ''} added`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remaining = cart ? cart.remaining : 0;
  const selectable = results.filter(r => !inCart.has(r.e164));

  return (
    <div className="space-y-3">
      <form onSubmit={search} className="flex flex-wrap gap-2 items-end">
        <div className="flex rounded-md border border-border overflow-hidden text-xs">
          {[['area_code', 'Area code'], ['state', 'State'], ['city', 'City']].map(([k, l]) => (
            <button type="button" key={k} onClick={() => setMode(k)}
              className={`px-3 h-9 ${mode === k ? 'bg-violet-500/15 text-violet-600 dark:text-violet-400' : 'text-muted-foreground'}`}>{l}</button>
          ))}
        </div>
        {mode !== 'state' && (
          <Input value={query} onChange={e => setQuery(e.target.value)} className="w-40 h-9"
            placeholder={mode === 'area_code' ? 'e.g. 916' : 'City name'} data-testid="ts-number-query" />
        )}
        {mode !== 'area_code' && (
          <select value={state} onChange={e => setState(e.target.value)} className="h-9 rounded-md border border-border bg-background px-2 text-sm">
            {US_STATES.map(s => <option key={s}>{s}</option>)}
          </select>
        )}
        <Button type="submit" size="sm" disabled={busy} data-testid="ts-number-search"><Search className="w-4 h-4 mr-1" /> Search</Button>
      </form>

      {message && <p className="text-xs text-amber-700 dark:text-amber-400">{message}</p>}

      {results.length > 0 && (
        <>
          {!onPick && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>{remaining} more number{remaining === 1 ? '' : 's'} needed.</span>
              <Button size="sm" variant="outline" disabled={busy || !selectable.length || remaining <= 0}
                onClick={() => add(selectable.slice(0, remaining).map(r => r.e164))} data-testid="ts-add-first">
                Add first {Math.min(remaining, selectable.length)}
              </Button>
              <Button size="sm" disabled={busy || !selected.length} onClick={() => add(selected)} data-testid="ts-add-selected">
                Add {selected.length} selected
              </Button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="ts-number-results">
            {results.map(n => {
              const taken = inCart.has(n.e164);
              return (
                <div key={n.e164} className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${selected.includes(n.e164) ? 'border-violet-500' : 'border-border'}`}>
                  {!onPick && (
                    <input type="checkbox" disabled={taken} checked={taken || selected.includes(n.e164)} onChange={() => toggle(n)} aria-label={`Select ${n.display}`} />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground">{n.display}</div>
                    <div className="text-xs text-muted-foreground truncate">{[n.locality, n.region].filter(Boolean).join(', ')}</div>
                  </div>
                  {onPick
                    ? <Button size="sm" variant="outline" onClick={() => onPick(n.e164)}>Use this</Button>
                    : taken ? <Pill tone="green">In cart</Pill>
                      : <Button size="sm" variant="ghost" disabled={busy || remaining <= 0} onClick={() => add([n.e164])} aria-label={`Add ${n.display}`}><Plus className="w-4 h-4" /></Button>}
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground">Numbers are offered by the carrier in real time and are checked again right before purchase. If one is gone, you choose the replacement — we never swap numbers for you.</p>
        </>
      )}
    </div>
  );
}

export function CartPanel({ cart, onCart }) {
  const [busy, setBusy] = useState(false);
  const remove = async (num) => {
    setBusy(true);
    try { onCart(await ts.cartRemove([num])); } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  if (!cart) return null;
  return (
    <Section title={`Selected seat numbers (${cart.numbers_selected} of ${cart.numbers_needed})`}>
      <div className="flex flex-wrap gap-1">
        {cart.area_codes.map(a => <Pill key={a.area_code} tone="blue">{a.area_code}: {a.count}</Pill>)}
      </div>
      {cart.items.filter(i => i.role !== 'main').length === 0 ? <Empty>No numbers selected yet.</Empty> : (
        <ul className="divide-y divide-border" data-testid="ts-cart">
          {cart.items.filter(i => i.role !== 'main').map(i => (
            <li key={i.e164} className="flex items-center justify-between py-1.5 text-sm">
              <span>{i.display} <span className="text-xs text-muted-foreground">{i.locality}</span></span>
              <button disabled={busy} onClick={() => remove(i.e164)} className="text-muted-foreground hover:text-red-500" aria-label={`Remove ${i.display}`}><X className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
